import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { parsePastedTable, coerceCellValue } from "@/lib/parsePaste";
import { MAX_ROWS, COLUMNS, DRAFT_KEY, emptyRow } from "@/components/settings/bulkTaxonomyHelpers";

const safeLocalStorage = (op) => {
  try { op(); } catch (e) {
    if (typeof console !== "undefined") console.warn("[bulk-dialog] localStorage:", e?.message || e);
  }
};

function toBody(r) {
  return {
    main_category: r.main_category || "",
    sub_category: r.sub_category || "",
    issue_type: r.issue_type || "",
    department: r.department || "",
    case_type: r.case_type || "",
    priority: r.priority || "medium",
    sla_minutes: r.sla_minutes || "480",
    branch_required: r.branch_required === "true" || r.branch_required === true,
  };
}

/**
 * Owns grid rows, validation results, lookups (departments, case types),
 * draft autosave, paste handling, and the validate/import/load/download API calls.
 */
export function useBulkTaxonomyGrid({ open, onImported, onOpenChange }) {
  const [rows, setRows] = useState([emptyRow()]);
  const [depts, setDepts] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [validation, setValidation] = useState(null);
  const [mode, setMode] = useState("skip");
  const [partial, setPartial] = useState(false);
  const [autoCreate, setAutoCreate] = useState(false);
  const [busy, setBusy] = useState(false);

  // Restore draft on first open
  useEffect(() => {
    if (!open) return;
    try {
      const stored = localStorage.getItem(DRAFT_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed.some((r) => r.main_category || r.issue_type)) {
          setRows(parsed);
        }
      }
    } catch (e) {
      if (typeof console !== "undefined") console.warn("[bulk-dialog] localStorage:", e?.message || e);
    }
  }, [open]);

  // Autosave draft
  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(() => {
      safeLocalStorage(() => localStorage.setItem(DRAFT_KEY, JSON.stringify(rows)));
    }, 600);
    return () => clearTimeout(handle);
  }, [rows, open]);

  // Load lookups
  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/departments"), api.get("/case-types")]).then(([d, c]) => {
      setDepts(d.data.filter((x) => x.active));
      setCaseTypes(c.data.filter((x) => x.active));
    });
  }, [open]);

  // Derived per-row error/status maps
  const errorsByRow = useMemo(() => {
    if (!validation) return {};
    const m = {};
    for (const e of (validation.errors || [])) {
      const r = e.row_index;
      m[r] = m[r] || {};
      m[r][e.column] = e.message;
    }
    return m;
  }, [validation]);

  const statusByRow = useMemo(() => {
    if (!validation) return {};
    const m = {};
    for (const p of validation.preview || []) m[p.row_index] = p.status;
    return m;
  }, [validation]);

  const firstErrorRow = useMemo(() => {
    const idxs = Object.keys(errorsByRow).map(Number).filter((n) => !Number.isNaN(n));
    return idxs.length ? Math.min(...idxs) : -1;
  }, [errorsByRow]);

  const updateCell = (rowIdx, colKey, value) => {
    setRows((prev) => prev.map((r, i) => i === rowIdx ? { ...r, [colKey]: value } : r));
    if (validation) setValidation(null);
  };

  const addRow = () => {
    if (rows.length >= MAX_ROWS) {
      toast.error(`Row limit (${MAX_ROWS}) reached`);
      return;
    }
    setRows((prev) => [...prev, emptyRow()]);
  };

  const deleteRow = (i) => {
    if (rows.length === 1) {
      setRows([emptyRow()]);
    } else {
      setRows((prev) => prev.filter((_, idx) => idx !== i));
    }
    setValidation(null);
  };

  const clearAll = () => {
    if (rows.length > 1 || (rows[0] && Object.values(rows[0]).some((v) => v && v !== "medium" && v !== "480" && v !== "false"))) {
      if (!window.confirm("Clear all rows? Unsaved work in this grid will be lost.")) return;
    }
    setRows([emptyRow()]);
    setValidation(null);
    safeLocalStorage(() => localStorage.removeItem(DRAFT_KEY));
  };

  const onPaste = (e, targetRow, targetCol) => {
    const text = e.clipboardData?.getData("text");
    if (!text) return;
    if (!text.includes("\t") && !text.includes("\n")) return;  // single cell → default
    e.preventDefault();
    const { rows: pasted, hasHeader } = parsePastedTable(text);
    const dataRows = hasHeader ? pasted.slice(1) : pasted;
    if (!dataRows.length) return;

    const colIdx = COLUMNS.findIndex((c) => c.key === targetCol);
    if (colIdx < 0) return;

    setRows((prev) => {
      const next = [...prev];
      dataRows.forEach((rowVals, dy) => {
        const tgtRowIdx = targetRow + dy;
        while (next.length <= tgtRowIdx) next.push(emptyRow());
        if (tgtRowIdx >= MAX_ROWS) return;
        const updated = { ...next[tgtRowIdx] };
        rowVals.forEach((cellVal, dx) => {
          const c = COLUMNS[colIdx + dx];
          if (!c) return;
          updated[c.key] = coerceCellValue(c.key, cellVal);
        });
        next[tgtRowIdx] = updated;
      });
      return next.slice(0, MAX_ROWS);
    });

    const truncated = dataRows.length + targetRow > MAX_ROWS ? ` (truncated to first ${MAX_ROWS} rows)` : "";
    toast.success(`Pasted ${Math.min(dataRows.length, MAX_ROWS - targetRow)} rows${hasHeader ? " · header skipped" : ""}${truncated}`);
    setValidation(null);
  };

  const validate = async () => {
    setBusy(true);
    try {
      const payload = { rows: rows.map(toBody), auto_create_lookups: autoCreate };
      const { data } = await api.post("/taxonomy/bulk-validate", payload);
      setValidation(data);
      const s = data.summary;
      toast.success(`Validated · ${s.rows_valid} valid · ${s.rows_invalid} error${s.rows_invalid === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
    } finally { setBusy(false); }
  };

  const runImport = async () => {
    if (mode === "replace" && !window.confirm("Replace mode WIPES all existing categories, sub-categories and issue types before importing. Continue?")) return;
    setBusy(true);
    try {
      const payload = {
        rows: rows.map(toBody), mode,
        auto_create_lookups: autoCreate, allow_partial: partial,
      };
      const { data } = await api.post("/taxonomy/bulk-import", payload);
      const c = data.created;
      toast.success(`Imported · ${c.main} main · ${c.sub} sub · ${c.issue} issue · ${data.updated.issue} updated · ${data.skipped} skipped`);
      safeLocalStorage(() => localStorage.removeItem(DRAFT_KEY));
      setRows([emptyRow()]);
      setValidation(null);
      onOpenChange(false);
      onImported?.();
    } catch (e) {
      if (e?.response?.status === 422 && e.response.data?.detail) {
        setValidation(e.response.data.detail);
        toast.error("Validation errors — review red cells");
      } else {
        toast.error(e?.response?.data?.detail || "Import failed");
      }
    } finally { setBusy(false); }
  };

  const downloadCurrent = async () => {
    try {
      const res = await fetch(`${API}/taxonomy/export`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("kippu_token")}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "kippu-taxonomy.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const loadCurrent = async () => {
    try {
      const res = await fetch(`${API}/taxonomy/export`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("kippu_token")}` },
      });
      const text = await res.text();
      const lines = text.replace(/\r\n/g, "\n").split("\n").filter(Boolean);
      const dataLines = lines.slice(1);
      const next = dataLines.slice(0, MAX_ROWS).map((line) => {
        const cols = line.split(",");
        return {
          main_category: (cols[0] || "").replace(/^"|"$/g, ""),
          sub_category: (cols[1] || "").replace(/^"|"$/g, ""),
          issue_type: (cols[2] || "").replace(/^"|"$/g, ""),
          department: (cols[3] || "").replace(/^"|"$/g, ""),
          case_type: (cols[4] || "").replace(/^"|"$/g, ""),
          priority: (cols[5] || "medium").replace(/^"|"$/g, ""),
          sla_minutes: (cols[6] || "480").replace(/^"|"$/g, ""),
          branch_required: (cols[7] || "false").replace(/^"|"$/g, ""),
        };
      });
      setRows(next.length ? next : [emptyRow()]);
      setValidation(null);
      toast.success(`Loaded ${next.length} rows from current taxonomy`);
    } catch { toast.error("Load failed"); }
  };

  const summary = validation?.summary;
  const canImport = rows.some((r) => r.main_category && r.sub_category && r.issue_type)
    && (!summary || summary.rows_invalid === 0 || partial);

  return {
    rows, depts, caseTypes,
    validation, summary, errorsByRow, statusByRow, firstErrorRow,
    mode, setMode, partial, setPartial, autoCreate, setAutoCreate,
    busy, canImport,
    updateCell, addRow, deleteRow, clearAll, onPaste,
    validate, runImport, downloadCurrent, loadCurrent,
  };
}
